import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  ComponentRef,
  createComponent,
  DOCUMENT,
  EnvironmentInjector,
  inject,
  OnDestroy,
  OnInit,
  signal
} from '@angular/core';
import { Title } from '@angular/platform-browser';
import { AuthService } from '@customer/services';
import { LightboxService, SpinnerService, StorageService, StrapiService } from '@core/services';
import { Account as AccountService } from '@customer/account/account.svc';
import { Contact as ContactService } from '@customer/contact/contact.svc';
import { BehaviorSubject, filter, Observable } from 'rxjs';
import { BnNgIdleService } from 'bn-ng-idle'; // session timeout handling
import { Event, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from 'oneok/environments/environment';
import { LightboxComponent, LoaderComponent, NotificationComponent, OrientationComponent } from '@core/components';
import { HeaderComponent, SidebarComponent } from '@core/components/chrome';
import { User } from 'oidc-client';
import posthog from 'posthog-js';
import { AsyncPipe } from '@angular/common';
import { AppOrientation } from '@legacy-models/strapi/AppOrientation';
import { SessionAuth } from '@legacy-models/auth/user';
import { Account } from '@legacy-models/portal/Account';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'oneok-customer-portal',
  imports: [
    RouterOutlet,
    HeaderComponent,
    SidebarComponent,
    LoaderComponent,
    NotificationComponent,
    OrientationComponent,
    AsyncPipe
  ],
  templateUrl: './customer-portal.html',
  styleUrl: './customer-portal.scss'
})
export class CustomerPortal implements OnInit, OnDestroy, AfterViewInit {
  baseUrl = environment.apiUrl;
  title = 'ONEOK Customer Portal';
  private readonly _doc = inject(DOCUMENT);
  private readonly _titleSvc = inject(Title);
  private readonly _contactSvc = inject(ContactService);
  private readonly _bnNgIdleSvc = inject(BnNgIdleService);
  private readonly _routerSvc = inject(Router);
  private readonly _lightboxSvc = inject(LightboxService);
  readonly accountSvc = inject(AccountService);
  readonly strapiSvc = inject(StrapiService);
  readonly authSvc = inject(AuthService);
  readonly spinnerSvc = inject(SpinnerService);
  readonly storageSvc = inject(StorageService);
  readonly changeDetector = inject(ChangeDetectorRef);
  private readonly _navigationEnd$: Observable<NavigationEnd>;
  private readonly _appRef = inject(ApplicationRef);
  private readonly _injector = inject(EnvironmentInjector);
  private _componentRef!: ComponentRef<LightboxComponent>;
  private _previousUrl: string | null = null;
  private readonly _$loadId = signal<string | null>(null);
  protected $loadId = this._$loadId.asReadonly();
  currentUser: SessionAuth;
  currentAccount: Account;
  private readonly _backgroundImageSource = new BehaviorSubject<string | null>(null);
  backgroundImage$ = this._backgroundImageSource.asObservable();
  math = Math;
  sessionTimeoutInterval = parseInt(environment.sessionTimeoutInterval ? environment.sessionTimeoutInterval : '1200');

  constructor() {
    this._bnNgIdleSvc.startWatching(this.sessionTimeoutInterval).subscribe(res => {
      if (res) {
        this._lightboxSvc.close();
        const currUser = JSON.parse(sessionStorage.getItem('currUser') as string);
        if (currUser) {
          this.authSvc.logoutUser();
        }
        this._routerSvc.navigate(['/login']);
      }
    });
    this._navigationEnd$ = this._routerSvc.events.pipe(
      filter((evt: Event) => evt instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;
  }

  onActivate(evt: any): void {
    const isBackgroundImage = 'hasBackgroundImg' in evt;
    let backgroundImageSrc;
    if (isBackgroundImage) {
      backgroundImageSrc = evt.backgroundImgSegment;
    } else {
      this._backgroundImageSource.next(null);
    }
    if (backgroundImageSrc === 'nggp' && !!sessionStorage.getItem('strapiBackgroundImages')) {
      this._backgroundImageSource.next(
        JSON.parse(sessionStorage.getItem('strapiBackgroundImages') as string).NggpBackgroundImage
      );
    } else if (backgroundImageSrc === 'nggp') {
      this._backgroundImageSource.next('/assets/images/facility--gp.png');
    } else if (backgroundImageSrc === 'ngp' && !!sessionStorage.getItem('strapiBackgroundImages')) {
      this._backgroundImageSource.next(
        JSON.parse(sessionStorage.getItem('strapiBackgroundImages') as string).NgpBackgroundImage
      );
    } else if (backgroundImageSrc === 'ngp') {
      this._backgroundImageSource.next('/assets/images/ngp_mustang.png');
    }
  }

  private _onNavigationEnd(evt: NavigationEnd): void {
    if (this._previousUrl && !this._previousUrl.includes('/login')) {
      posthog.capture('$pageleave', {
        $current_url: evt.urlAfterRedirects,
        $referrer: this._previousUrl
      });
    }
    posthog.capture('$pageview');
    this._previousUrl = evt.urlAfterRedirects;
    /**
     * The below could be … better
     * > It determines if the _returnRoute sessionStorage value should be deleted or not
     * > Different scenarios call for different behavior, and
     * > what exists below is a quick-n-dirty solution (but it works!)
     */
    const _is = ['/gathering-and-processing', '/intrastate-pipelines'].some((urlSegment: string) =>
      evt.urlAfterRedirects.endsWith(urlSegment)
    );
    if (
      !!sessionStorage.getItem('_returnRoute') &&
      (_is || (!evt.urlAfterRedirects.endsWith('/login') && !evt.urlAfterRedirects.includes('session')))
    ) {
      sessionStorage.removeItem('_returnRoute');
    }
  }

  private _onInternalUsersLoginSubscription(user: User | void): void {
    if (user) {
      this.authSvc.setSecureAuthUser(user);
    }
  }

  ngOnInit(): void {
    this.authSvc.completeInternalUsersLogin().subscribe({
      next: this._onInternalUsersLoginSubscription.bind(this)
    });
    this._initializeDynamicLightbox();
    this.setCurrentUser();
    this.setCurrentAccount();
    this.setLoggedInUser();
    let siteTitlePrefix = '';
    if (window.location.href.indexOf('.dynlab.oneok.com') >= 0) {
      siteTitlePrefix = 'DEV: ';
    } else if (window.location.href.indexOf('.dyntest.oneok.com') >= 0) {
      siteTitlePrefix = 'TEST: ';
    } else if (window.location.href.indexOf('dynqa.oneok.com') >= 0) {
      siteTitlePrefix = 'QA: ';
    } else if (window.location.href.indexOf('localhost') >= 0) {
      siteTitlePrefix = 'LOCAL: ';
    }
    this._titleSvc.setTitle(`${siteTitlePrefix}ONEOK Customer Portal`);
    this._navigationEnd$.subscribe(this._onNavigationEnd);
  }

  getBackgroundImages(): void {
    const strapiBackgroundImages = JSON.parse(sessionStorage.getItem('strapiBackgroundImages') as string);
    if (!strapiBackgroundImages) {
      this.strapiSvc.getBackgroundImages().subscribe(backgroundImages => {
        sessionStorage.setItem('strapiBackgroundImages', JSON.stringify(backgroundImages));
        document.body.style.setProperty('--bgImgSrc', 'url(' + backgroundImages.UnauthenticatedBackgroundImage + ')');
      });
    } else {
      document.body.style.setProperty(
        '--bgImgSrc',
        'url(' + strapiBackgroundImages.UnauthenticatedBackgroundImage + ')'
      );
    }
  }

  ngOnDestroy(): void {
    this.storageSvc.clearAppStorage();
    if (this._componentRef) {
      this._appRef.detachView(this._componentRef.hostView);
      this._componentRef.destroy();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      this.spinnerSvc.apiLoading$.subscribe(apiInfo => {
        this._$loadId.set(apiInfo.loadId);
      });
      this.storageSvc.setCurrentPage(this.storageSvc.currentPageSource.value as string);
    });
  }

  isNakedView(): boolean {
    const currentUrl = location.pathname;
    const isInterestedUrl = ['/interested', '/playground'].some(u => currentUrl.includes(u));
    const isIt = isInterestedUrl;
    return isIt;
  }

  isNotificationVisible({
    url,
    meta,
    isViewStatusSaved: isPersistent
  }: {
    url: string;
    meta: any[];
    isViewStatusSaved: boolean;
  }): boolean {
    const currentPageUrl = location.pathname
      .split('/')
      .map(x => x.toLowerCase())
      .filter((x, y) => y !== 0)
      .join('/');
    const isMatchedUrl = url ? currentPageUrl === url : true;
    const isAcknowledged = isPersistent ? meta.some(({ id }) => id === this.strapiSvc.appUserId) : false;
    return isMatchedUrl && !isAcknowledged;
  }

  isOrientationVisible({ Url: url, Meta: meta }: AppOrientation): boolean {
    const currentPageUrl = location.pathname
      .split('/')
      .map(x => x.toLowerCase())
      .filter((x, y) => y !== 0)
      .join('/');
    const isMatchedUrl = currentPageUrl === url;
    const isAcknowledged = meta.some(({ id }) => id === this.strapiSvc.appUserId);
    return isMatchedUrl && !isAcknowledged;
  }

  onNotificationAcknowledged({ id: notificationId, isPersist }: { id: number; isPersist: boolean }): void {
    this.strapiSvc.notifications = this.strapiSvc.notifications.filter(
      notification => notification.id !== notificationId
    );
    if (!this.storageSvc.currentUserSource.getValue() || !isPersist) {
      return;
    }

    this.strapiSvc.doAppNotificationUpdate(notificationId).subscribe({
      next: response => {
        // Success - acknowledgment saved
      },
      error: error => {
        console.error('Failed to save notification acknowledgment:', error);
      }
    });
  }

  onOrientationAcknowledged(appOrientationId: number): void {
    this.strapiSvc.orientations = this.strapiSvc.orientations.filter(
      appOrientation => appOrientation.id !== appOrientationId
    );
    this.strapiSvc.doAppOrientationUpdate(appOrientationId).subscribe();
  }

  setCurrentUser(): void {
    const currUser = JSON.parse(sessionStorage.getItem('currUser') as string);
    if (currUser) {
      this.currentUser = currUser;
      this.storageSvc.setCurrentUser(currUser);
      this.refreshCurrentUser(currUser);
      if (!!this.strapiSvc.orientations === true) {
        return;
      }
      if (currUser.AppUser) {
        this.strapiSvc
          .initAppUser({
            CrmId: currUser.AppUser.CrmId,
            Id: currUser.AppUser.Id
          })
          .then(isSuccess => {
            this._contactSvc.handleCmsUser();
          });
      } else {
        this._contactSvc.handleCmsUser();
      }
    }
  }

  setLoggedInUser(): void {
    const user = JSON.parse(sessionStorage.getItem('loggedInUser') as string);
    if (user) {
      this.storageSvc.setloggedInUser(user);
    }
  }

  setVersion(data: string): void {
    const logVersion = JSON.parse(sessionStorage.getItem('version') as string);
    if (logVersion) {
      this.storageSvc.setVersion(data);
    }
  }

  setCurrentAccount(): void {
    const currAcc = JSON.parse(sessionStorage.getItem('currAcct') as string);

    if (currAcc) {
      this.storageSvc.storeCurrentAccount(currAcc);
    }
  }

  refreshCurrentUser(currUser: SessionAuth): void {
    this._contactSvc
      .getAccountModulesForContact(currUser.CurrAccountId as string, currUser.Contact!.Email as string)
      .subscribe(modules => {
        currUser.Contact!.Modules = modules;
        this.storageSvc.setCurrentUser(currUser);
      });
  }

  private _initializeDynamicLightbox(): void {
    this._componentRef = createComponent(LightboxComponent, {
      environmentInjector: this._injector
    });
    this._appRef.attachView(this._componentRef.hostView);
    const dialogElem = (this._componentRef.hostView as any).rootNodes[0] as HTMLDialogElement;
    const appRoot = this._doc.querySelector('oneok-customer-portal');
    if (appRoot && appRoot.parentNode) {
      appRoot.parentNode.insertBefore(dialogElem, appRoot.nextSibling);
    }
  }
}
